import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

export default function FAQPage() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const faqData: FAQItem[] = [
    // Reservas e Alterações
    {
      category: 'reservas',
      question: t('faqQuestion1'),
      answer: t('faqAnswer1')
    },
    {
      category: 'reservas',
      question: t('faqQuestion2'),
      answer: t('faqAnswer2')
    },
    {
      category: 'reservas',
      question: t('faqQuestion3'),
      answer: t('faqAnswer3')
    },
    {
      category: 'reservas',
      question: t('faqQuestion4'),
      answer: t('faqAnswer4')
    },
    // Aeroporto e Encontro
    {
      category: 'aeroporto',
      question: t('faqQuestion5'),
      answer: t('faqAnswer5')
    },
    {
      category: 'aeroporto',
      question: t('faqQuestion6'),
      answer: t('faqAnswer6')
    },
    {
      category: 'aeroporto',
      question: t('faqQuestion7'),
      answer: t('faqAnswer7')
    },
    // Bagagem e Extras
    {
      category: 'bagagem',
      question: t('faqQuestion8'),
      answer: t('faqAnswer8')
    },
    {
      category: 'bagagem',
      question: t('faqQuestion9'),
      answer: t('faqAnswer9')
    },
    {
      category: 'bagagem',
      question: t('faqQuestion10'),
      answer: t('faqAnswer10')
    },
    // Tipos de Serviço
    {
      category: 'servico',
      question: t('faqQuestion11'),
      answer: t('faqAnswer11')
    },
    {
      category: 'servico',
      question: t('faqQuestion12'),
      answer: t('faqAnswer12')
    },
    {
      category: 'servico',
      question: t('faqQuestion13'),
      answer: t('faqAnswer13')
    },
    // Pagamento e Preços
    {
      category: 'pagamento',
      question: t('faqQuestion14'),
      answer: t('faqAnswer14')
    },
    {
      category: 'pagamento',
      question: t('faqQuestion15'),
      answer: t('faqAnswer15')
    },
    {
      category: 'pagamento',
      question: t('faqQuestion16'),
      answer: t('faqAnswer16')
    },
    // Segurança e Confiança
    {
      category: 'seguranca',
      question: t('faqQuestion17'),
      answer: t('faqAnswer17')
    },
    {
      category: 'seguranca',
      question: t('faqQuestion18'),
      answer: t('faqAnswer18')
    },
    {
      category: 'seguranca',
      question: t('faqQuestion19'),
      answer: t('faqAnswer19')
    }
  ];

  const categories = [
    { id: 'all', name: t('faqCategoryAll'), icon: 'ri-question-line' },
    { id: 'reservas', name: t('faqCategoryReservas'), icon: 'ri-calendar-check-line' },
    { id: 'aeroporto', name: t('faqCategoryAeroporto'), icon: 'ri-plane-line' },
    { id: 'bagagem', name: t('faqCategoryBagagem'), icon: 'ri-luggage-cart-line' },
    { id: 'servico', name: t('faqCategoryServico'), icon: 'ri-car-line' },
    { id: 'pagamento', name: t('faqCategoryPagamento'), icon: 'ri-bank-card-line' },
    { id: 'seguranca', name: t('faqCategorySeguranca'), icon: 'ri-shield-check-line' }
  ];

  const filteredFAQs = selectedCategory === 'all' 
    ? faqData 
    : faqData.filter(faq => faq.category === selectedCategory);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-question-answer-line text-5xl text-white"></i>
          </div>
          <h1 className="text-5xl font-bold mb-6">
            {t('faqTitle')}
          </h1>
          <p className="text-xl text-orange-100 max-w-3xl mx-auto">
            {t('faqSubtitle')}
          </p>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap flex items-center gap-2 ${
                  selectedCategory === category.id
                    ? 'bg-orange-500 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-orange-50 hover:text-orange-600 shadow-sm'
                }`}
              >
                <i className={`${category.icon} text-lg`}></i>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-question-line text-orange-600 text-xl"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 flex-1">
                      {faq.question}
                    </h3>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <i
                      className={`ri-arrow-down-s-line text-2xl text-gray-400 transition-transform ${
                        openIndex === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </div>
                </button>
                
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-6 pl-20">
                    <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredFAQs.length === 0 && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-4xl text-gray-400"></i>
              </div>
              <p className="text-gray-500 text-lg">
                {t('faqNoResults')}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-orange-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-customer-service-2-line text-3xl text-white"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {t('faqStillHaveQuestions')}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('faqContactText')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/contactos"
              className="px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-mail-line text-xl"></i>
              {t('faqContactButton')}
            </a>
            <a
              href="tel:+351937545000"
              className="px-8 py-4 bg-white hover:bg-gray-50 text-orange-600 border-2 border-orange-500 rounded-lg font-semibold transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-phone-line text-xl"></i>
              +351 937 545 000
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
